/**
 * Single HTTP client for the BGH backend. Every network call in the app goes
 * through here so auth, error normalisation, and JSON handling live in one
 * place instead of being re-implemented per service.
 */

const configuredBaseUrl = (
  (import.meta.env.VITE_API_URL as string) || 'http://localhost:3000/api/v1'
).replace(/\/$/, '');

// Accept either a host or a complete API base in deployment configuration.
// This prevents a host-only VITE_API_URL from silently posting to /auth/*
// instead of the versioned /api/v1/auth/* routes.
export const BASE_URL = configuredBaseUrl.endsWith('/api/v1')
  ? configuredBaseUrl
  : `${configuredBaseUrl}/api/v1`;

import { AUTH_PERMISSIONS_KEY, AUTH_ROLE_KEY, AUTH_USER_KEY, REFRESH_KEY, TOKEN_KEY } from './authStorage';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setRefreshToken(token: string | null): void {
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

export function clearAuthSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_ROLE_KEY);
  localStorage.removeItem(AUTH_PERMISSIONS_KEY);
}

/**
 * The access token is short-lived (15m). Rather than log the user out the moment
 * it expires mid-session, we swap the stored refresh token for a fresh pair.
 * Single-flight: concurrent 401s share one refresh call.
 */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * The one place a refresh is started. Every caller awaits the same promise,
 * and only the promise itself clears the slot — clearing it per awaiter let a
 * later call start a second refresh with the token the first had already
 * rotated away, which the backend correctly reads as token theft and answers
 * by revoking every session the user has.
 */
function startRefresh(): Promise<string | null> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** Public single-flight refresh — returns true if a fresh access token was obtained. */
export async function refreshSession(): Promise<boolean> {
  return Boolean(await startRefresh());
}

async function refreshAccessToken(): Promise<string | null> {
  const rt = getRefreshToken();
  if (!rt) return null;
  const activeRoleKey = rolePreviewFromToken(getToken());
  try {
    const res = await fetch(buildUrl('/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt, activeRoleKey }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { accessToken?: string; refreshToken?: string };
    if (!data?.accessToken) return null;
    setToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    return data.accessToken;
  } catch {
    return null;
  }
}

/** Read only signed JWT claims needed to preserve a role-preview during refresh. */
function rolePreviewFromToken(token: string | null): string | undefined {
  if (!token) return undefined;
  try {
    const [, encodedPayload] = token.split('.');
    const payload = JSON.parse(
      atob(encodedPayload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { roleKey?: unknown; baseRoleKey?: unknown };
    const roleKey = typeof payload.roleKey === 'string' ? payload.roleKey : undefined;
    const baseRoleKey =
      typeof payload.baseRoleKey === 'string' ? payload.baseRoleKey : undefined;
    return roleKey && baseRoleKey && roleKey !== baseRoleKey ? roleKey : undefined;
  } catch {
    return undefined;
  }
}

/** A normalised API failure — `message` is always human-readable and toast-safe. */
export class ApiError extends Error {
  readonly status: number;
  readonly body?: unknown;
  readonly retryAfterSeconds?: number;

  constructor(
    status: number,
    message: string,
    body?: unknown,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Nest returns { statusCode, message: string | string[], error }. Flatten it. */
function extractMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const m = (body as { message?: unknown }).message;
    if (Array.isArray(m)) return m.join(', ');
    if (typeof m === 'string') return m;
  }
  return fallback;
}

export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

function buildUrl(path: string, query?: QueryParams): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `${url}?${qs}` : url;
}

interface RequestOptions {
  query?: QueryParams;
  /** Set false for public endpoints so a stale token isn't sent. */
  auth?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
  allowRefresh = true,
): Promise<T> {
  const headers: Record<string, string> = {};
  const isFormData =
    typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getToken();
  if (token && opts.auth !== false) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body:
        body === undefined
          ? undefined
          : isFormData
            ? (body as FormData)
            : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      0,
      'Cannot reach the server. Check that the backend is running.',
    );
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data: unknown = text ? safeJson(text) : undefined;

  if (!res.ok) {
    console.warn('[api] request failed', {
      method,
      path,
      status: res.status,
      retryAfter: res.headers.get('retry-after'),
    });
    // Only a 401 on a call that actually carried our credentials says anything
    // about the session. A public endpoint rejecting an unrelated request must
    // never sign the user out.
    if (res.status === 401 && opts.auth !== false) {
      // Try to silently refresh the access token before ending the session.
      if (allowRefresh && path !== '/auth/refresh') {
        if (await startRefresh()) {
          return request<T>(method, path, body, opts, false);
        }
      }
      clearAuthSession();
      window.dispatchEvent(new CustomEvent('auth:expired', { detail: { status: 401, path } }));
      if (window.location.pathname !== `${__BASE_PATH__}login`) {
        const basePath = __BASE_PATH__.replace(/\/$/, '');
        window.location.replace(`${basePath}/login?reason=session-expired`);
      }
    }
    const retryAfter = Number(res.headers.get('retry-after'));
    throw new ApiError(
      res.status,
      extractMessage(data, res.statusText),
      data,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
  }
  return data as T;
}

/** Binary GET (PDF exports). Mirrors `request`, minus the JSON parsing. */
async function requestBlob(
  path: string,
  opts: RequestOptions = {},
  allowRefresh = true,
): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token && opts.auth !== false) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), { method: 'GET', headers });
  } catch {
    throw new ApiError(
      0,
      'Cannot reach the server. Check that the backend is running.',
    );
  }

  if (!res.ok) {
    if (res.status === 401 && allowRefresh && opts.auth !== false) {
      if (await refreshSession()) return requestBlob(path, opts, false);
    }
    const text = await res.text();
    throw new ApiError(
      res.status,
      extractMessage(text ? safeJson(text) : undefined, res.statusText),
    );
  }
  return res.blob();
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) =>
    request<T>('GET', path, undefined, opts),
  getBlob: (path: string, opts?: RequestOptions) => requestBlob(path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions) =>
    request<T>('DELETE', path, undefined, opts),
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

/** For guest-facing surfaces: hides raw connectivity/server-outage text (status 0 or 5xx) behind a generic message, but still shows real backend validation errors (4xx). */
export function guestErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status !== 0 && err.status < 500) {
    return err.message;
  }
  return fallback;
}
